/**
 * Pre-login tenant branding/config resolved by browser hostname.
 * Backend: GET /client/portal-config?host={hostname}
 */

import { resolveStorageUrl, valtFetch, VALT_API_BASE } from '@/utils/api'
import { normalizePortalHost } from '@/utils/clientPortalDns'
import { isRegisteredPortalHostname, shouldFetchPortalConfig } from '@/utils/portalHost'

export interface PortalConfigFeatures {
  vehicle_features_enabled?: boolean
  bike_features_enabled?: boolean
}

export interface PortalConfig {
  client_id?: string | number
  company_name?: string
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  accent_color?: string
  favicon_url?: string
  login_hint?: string
  portal_host?: string
  /** Additional hostnames mapped to this client (when returned by the API). */
  custom_domains?: string[]
  features?: PortalConfigFeatures
}

function pickString(...values: unknown[]): string | undefined {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return undefined
}

function normalizeFeatures(raw: unknown): PortalConfigFeatures | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const o = raw as Record<string, unknown>
  return {
    vehicle_features_enabled:
      typeof o.vehicle_features_enabled === 'boolean'
        ? o.vehicle_features_enabled
        : typeof o.vehicleFeaturesEnabled === 'boolean'
          ? o.vehicleFeaturesEnabled
          : undefined,
    bike_features_enabled:
      typeof o.bike_features_enabled === 'boolean'
        ? o.bike_features_enabled
        : typeof o.bikeFeaturesEnabled === 'boolean'
          ? o.bikeFeaturesEnabled
          : undefined,
  }
}

/** Normalize API / metadata shapes into PortalConfig. */
export function normalizePortalConfig(raw: unknown): PortalConfig | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>
  const data = (r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : r) as Record<
    string,
    unknown
  >
  const md =
    data.metadata && typeof data.metadata === 'object' && !Array.isArray(data.metadata)
      ? (data.metadata as Record<string, unknown>)
      : {}

  const logo = pickString(data.logo_url, data.logoUrl, md.logo_url, md.logoUrl, md.logo_path, md.logoPath)
  const company = pickString(data.company_name, data.companyName, data.name)

  const config: PortalConfig = {
    client_id: (data.client_id ?? data.clientId ?? data.id ?? data.contractor_id) as string | number | undefined,
    company_name: company,
    logo_url: logo ? resolveStorageUrl(logo) : undefined,
    primary_color: pickString(data.primary_color, data.primaryColor, md.primary_color, md.primaryColor),
    secondary_color: pickString(data.secondary_color, data.secondaryColor, md.secondary_color, md.secondaryColor),
    accent_color: pickString(data.accent_color, data.accentColor, md.accent_color, md.accentColor),
    favicon_url: pickString(data.favicon_url, data.faviconUrl, md.favicon_url),
    login_hint: pickString(data.login_hint, data.loginHint),
    portal_host: pickString(data.portal_host, data.portalHost, data.host, data.portal_domain, data.portalDomain),
    custom_domains: parseAlternatePortalHosts(data.custom_domains ?? data.customDomains ?? md.custom_domains),
    features: normalizeFeatures(data.features ?? md.features ?? data),
  }

  const hasContent = Boolean(
    config.client_id ||
      config.company_name ||
      config.logo_url ||
      config.primary_color ||
      config.secondary_color ||
      config.accent_color,
  )
  return hasContent ? config : null
}

function parseAlternatePortalHosts(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.map((d) => normalizePortalHost(String(d))).filter(Boolean)
}

function getConfigAlternateHosts(config: PortalConfig): string[] {
  const hosts = new Set<string>()
  for (const domain of config.custom_domains ?? []) {
    hosts.add(normalizePortalHost(domain))
  }
  return [...hosts]
}

/** Accept config when the API mapped the requested browser hostname to this tenant. */
export function portalConfigMatchesHost(config: PortalConfig, requestedHost: string): boolean {
  const host = normalizePortalHost(requestedHost)
  if (!host) return false
  if (!config.client_id && !config.company_name) return false

  const mappedHost = config.portal_host ? normalizePortalHost(config.portal_host) : ''
  if (!mappedHost || mappedHost === host) return true

  if (getConfigAlternateHosts(config).includes(host)) return true

  // Multi-domain clients: API returns the canonical primary in portal_host while the
  // lookup used an alias (e.g. yassir.valt.africa vs yassir.client-apps.co.za).
  return isRegisteredPortalHostname(host)
}

export async function fetchPortalConfigByHost(hostname: string): Promise<PortalConfig | null> {
  const host = hostname.trim().toLowerCase()
  if (!host || !shouldFetchPortalConfig(host)) return null

  const url = `${VALT_API_BASE}/client/portal-config?host=${encodeURIComponent(host)}`
  try {
    const res = await valtFetch(url, { skipAuth: true, method: 'GET' })
    if (res.status === 404) return null
    const json = await res.json().catch(() => null)
    if (!res.ok) return null
    const config = normalizePortalConfig(json)
    if (!config || !portalConfigMatchesHost(config, host)) return null
    return config
  } catch {
    return null
  }
}
